import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../../services/api';
import ArborescenceLogo from '../../../components/ArborescenceLogo';
import PropertyMapEditor from '../../../components/PropertyMapEditor';
import ClientProfitabilityPanel from '../../../components/production/profitability/ClientProfitabilityPanel';
import { ArrowLeft, Plus, Send, Trash2, X, Download, Mail, Link as LinkIcon, Copy, Edit2 } from 'lucide-react';
import '../../../styles/admin/ClientDetail.css';


const ClientDetail = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showQuotes, setShowQuotes] = useState(true);
  
  // Nouveau devis
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [services, setServices] = useState([]);
  const [packages, setPackages] = useState([]); // Forfaits
  const [departments, setDepartments] = useState([]);
  const [showServiceSelector, setShowServiceSelector] = useState(false);
  const [quoteLines, setQuoteLines] = useState([]); // [{service_id, service_nom, quantite, prix_unitaire, departement_nom, departement_couleur, is_package, description}]
  const [premierIntervenant, setPremierIntervenant] = useState(false);
  const [modalitePaiement, setModalitePaiement] = useState('100_avant');

  useEffect(() => {
    fetchClientData();
  }, [clientId]);

  const fetchClientData = async () => {
    try {
      // Try both clients and submissions collections
      let clientRes;
      try {
        // Try new clients collection first
        clientRes = await api.get(
          `/api/clients/${clientId}`,
        );
        setClient(clientRes.data.client);
      } catch (err) {
        // Fallback to submissions (legacy)
        console.log('Trying legacy submissions endpoint...');
        clientRes = await api.get(
          `/api/admin/submissions/${clientId}`,
        );
        setClient(clientRes.data);
      }

      // Charger services, départements et devis du client en parallèle
      const [servicesRes, departmentsRes, quotesRes] = await Promise.all([
        api.get('/api/erp/services'),
        api.get('/api/erp/departments'),
        api.get(`/api/erp/quotes?client_id=${clientId}`)
      ]);
      
      const allServices = servicesRes.data.services || [];
      // Séparer services simples et forfaits
      const simpleServices = allServices.filter(s => !s.type_service || s.type_service === 'simple');
      const forfaits = allServices.filter(s => s.type_service === 'forfait');
      
      setServices(simpleServices);
      setPackages(forfaits);
      setDepartments(departmentsRes.data.departments || []);
      setQuotes(quotesRes.data.quotes || []);
    } catch (error) {
      if (error.response?.status === 401) {
        
        navigate('/auth/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleNewQuote = () => {
    setQuoteLines([]);
    setPremierIntervenant(false);
    setModalitePaiement('100_avant');
    setShowQuoteForm(true);
  };

  const handleAddServiceToQuote = (service) => {
    const defaultQuantite = service.type_tarification === 'pi2' && client.propertyArea 
      ? client.propertyArea 
      : 1;
    
    const dept = departments.find(d => d.id === service.department_id);
    
    // Calcul du prix unitaire basé sur la nouvelle structure
    let prix_unitaire = 0;
    if (service.prix_vente_par_1000_pi2 && service.type_tarification === 'pi2') {
      // Nouveau système: prix par 1000 pi²
      // Pour une propriété de X pi², le prix = (X / 1000) × prix_vente_par_1000_pi2
      prix_unitaire = service.prix_vente_par_1000_pi2 / 1000;  // Prix par pi²
    } else {
      // Legacy: utiliser prix_base
      prix_unitaire = service.prix_base || 0;
    }
    
    setQuoteLines([...quoteLines, {
      service_id: service.id,
      service_nom: service.nom,
      quantite: defaultQuantite,
      prix_unitaire: prix_unitaire,
      departement_nom: dept?.nom || 'N/A',
      departement_couleur: dept?.couleur || '#01500A',
      is_package: false,
      description: service.description || '' // Description par défaut du service
    }]);
    
    setShowServiceSelector(false);
  };

  const handleAddPackageToQuote = (forfait) => {
    const defaultQuantite = forfait.type_tarification === 'pi2' && client.propertyArea 
      ? client.propertyArea 
      : 1;
    
    const dept = departments.find(d => d.id === forfait.department_id);
    
    // Calcul du prix unitaire pour le forfait
    let prix_unitaire = 0;
    if (forfait.prix_vente_par_1000_pi2 && forfait.type_tarification === 'pi2') {
      prix_unitaire = forfait.prix_vente_par_1000_pi2 / 1000;  // Prix par pi²
    } else {
      prix_unitaire = forfait.prix_base || 0;
    }
    
    setQuoteLines([...quoteLines, {
      service_id: forfait.id,
      service_nom: `📦 ${forfait.nom}`, // Icône pour distinguer les forfaits
      quantite: defaultQuantite,
      prix_unitaire: prix_unitaire,
      departement_nom: dept?.nom || 'N/A',
      departement_couleur: dept?.couleur || '#01500A',
      is_package: true,
      package_services: forfait.services_in_package || [], // IDs des services inclus
      package_distribution: forfait.repartition_prix || [], // Distribution des prix
      description: forfait.description || '' // Description du forfait
    }]);
    
    setShowServiceSelector(false);
  };

  const handleRemoveLine = (index) => {
    setQuoteLines(quoteLines.filter((_, i) => i !== index));
  };

  const handleDownloadPDF = async (quoteId, quoteNumero) => {
    try {
      const response = await api.get(
        `/api/erp/quotes/${quoteId}/pdf`,
        { 
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${quoteNumero}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      alert('✅ PDF téléchargé avec succès!');
    } catch (error) {
      alert('❌ Erreur lors du téléchargement du PDF');
      console.error(error);
    }
  };

  const handleSendEmail = async (quoteId) => {
    if (!window.confirm('Envoyer ce devis par email au client?')) {
      return;
    }

    try {
      await api.post(
        `/api/erp/quotes/${quoteId}/send-email`,
        {},
      );
      
      alert('✅ Email envoyé avec succès!');
    } catch (error) {
      alert('❌ Erreur lors de l\'envoi de l\'email');
      console.error(error);
    }
  };

  const handleGeneratePublicLink = async (quoteId) => {
    try {
      const response = await api.post(
        `/api/erp/quotes/${quoteId}/generate-link`,
        {},
      );
      
      const publicUrl = response.data.public_url;
      const emailSent = response.data.email_sent;
      
      // Copy to clipboard
      navigator.clipboard.writeText(publicUrl);
      
      const message = emailSent 
        ? `✅ Lien généré et email envoyé au client!\n\n${publicUrl}\n\nLe lien a été copié dans le presse-papier.`
        : `✅ Lien copié!\n\n${publicUrl}\n\nVous pouvez maintenant le partager avec le client.`;
      
      alert(message);
      
      // Refresh to show updated quote with token
      fetchClientData();
    } catch (error) {
      alert('❌ Erreur lors de la génération du lien');
      console.error(error);
    }
  };

  const handleQuantityChange = (index, newQuantite) => {
    const updated = [...quoteLines];
    updated[index].quantite = parseFloat(newQuantite) || 0;
    setQuoteLines(updated);
  };

  const handlePriceChange = (index, newPrice) => {
    const updated = [...quoteLines];
    updated[index].prix_unitaire = parseFloat(newPrice) || 0;
    setQuoteLines(updated);
  };

  const handleDescriptionChange = (index, newDescription) => {
    const updated = [...quoteLines];
    updated[index].description = newDescription;
    setQuoteLines(updated);
  };

  const calculateTotals = () => {
    const subtotal = quoteLines.reduce((sum, line) => sum + (line.quantite * line.prix_unitaire), 0);
    const discount = premierIntervenant ? subtotal * 0.20 : 0;
    const afterDiscount = subtotal - discount;
    const tps = afterDiscount * 0.05;
    const tvq = afterDiscount * 0.09975;
    const total = afterDiscount + tps + tvq;
    
    return { subtotal, discount, afterDiscount, tps, tvq, total };
  };

  const handleSaveQuote = async () => {
    if (quoteLines.length === 0) {
      alert('Veuillez ajouter au moins un service');
      return;
    }

    try {
      const totals = calculateTotals();
      
      // Prepare services for API (with descriptions)
      const servicesForApi = quoteLines.map(line => ({
        service_id: line.service_id,
        quantite: line.quantite,
        unite: 'pi²',
        description: line.description || ''
      }));

      // Calculate quote via API
      const calcResponse = await api.post(
        '/api/erp/quotes/calculate',
        {
          client_id: clientId,
          services: servicesForApi,
          premier_intervenant: premierIntervenant,
          modalite_paiement: modalitePaiement
        },
      );

      // Create quote with BROUILLON status
      const createResponse = await api.post(
        '/api/erp/quotes',
        {
          client_id: clientId,  // ⚠️ CRITIQUE: client_id doit être inclus!
          ...calcResponse.data,
          statut: 'brouillon'
        },
      );

      alert('✅ Devis sauvegardé avec succès!');
      
      // Attendre le rechargement des données AVANT de fermer le formulaire
      await fetchClientData();
      
      setShowQuoteForm(false);
      setQuoteLines([]);
      
      return createResponse.data.quote.id;
    } catch (error) {
      alert('Erreur lors de la sauvegarde du devis');
      console.error(error);
      throw error;
    }
  };

  const handleDownloadNewQuotePDF = async () => {
    try {
      // Save quote first
      const quoteId = await handleSaveQuote();
      
      if (!quoteId) return;
      
      // Wait a moment for backend to process
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Download PDF
      const response = await api.get(
        `/api/erp/quotes/${quoteId}/pdf`,
        { 
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Devis-${client.numero_client}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      alert('✅ PDF téléchargé avec succès!');
    } catch (error) {
      alert('Erreur lors du téléchargement du PDF');
      console.error(error);
    }
  };

  const handleSendNewQuoteEmail = async () => {
    try {
      // Save quote first
      const quoteId = await handleSaveQuote();
      
      if (!quoteId) return;
      
      // Update status to ENVOYE
      await api.put(
        `/api/erp/quotes/${quoteId}/status`,
        { statut: 'envoye' },
      );
      
      // Send email
      await api.post(
        `/api/erp/quotes/${quoteId}/send-email`,
        {},
      );

      alert('✅ Devis envoyé par email avec succès!');
    } catch (error) {
      alert('Erreur lors de l\'envoi du devis par email');
      console.error(error);
    }
  };

  const handleSaveAndSendQuote = async () => {
    // Legacy function - redirect to handleSendEmail
    await handleSendEmail();
  };

  const groupServicesByDepartment = () => {
    const grouped = {};
    services.filter(s => s.actif && (!s.type_service || s.type_service === 'simple')).forEach(service => {
      const deptId = service.department_id || 'none';
      const dept = departments.find(d => d.id === deptId);
      const deptName = dept?.nom || 'AUTRE';
      
      if (!grouped[deptName]) {
        grouped[deptName] = {
          couleur: dept?.couleur || '#01500A',
          services: []
        };
      }
      grouped[deptName].services.push(service);
    });
    return grouped;
  };

  const totals = calculateTotals();

  if (loading) {
    return <div className="admin-loading">Chargement...</div>;
  }

  if (!client) {
    return <div className="admin-loading">Client non trouvé</div>;
  }

  return (
    <div className="client-detail-page">
      <div className="client-detail-header">
        <div className="client-detail-header-content">
          <Link to="/admin/erp/clients" className="back-link">
            <ArrowLeft size={20} />
            Retour aux clients
          </Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '1rem' }}>
            <ArborescenceLogo size="small" showText={false} />
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <span className="client-numero-badge">#{client.numero_client || 'N/A'}</span>
                <h1>Fiche Client - {client.prenom} {client.nom}</h1>
              </div>
              <p>Informations complètes et gestion des devis</p>
            </div>
          </div>
        </div>
      </div>

      <div className="client-detail-content">
        {/* Client Info Section */}
        <div className="client-info-section">
          <h2>📋 Informations du client</h2>
          <div className="info-grid">
            <div className="info-item">
              <strong>Email:</strong> {client.courriel}
            </div>
            <div className="info-item">
              <strong>Téléphone:</strong> {client.telephone || 'N/A'}
            </div>
            <div className="info-item">
              <strong>Adresse:</strong> {client.adresse}
            </div>
            <div className="info-item">
              <strong>Superficie:</strong> {client.propertyArea?.toLocaleString() || 'N/A'} pi²
            </div>
            {client.notes && (
              <div className="info-item full-width">
                <strong>Notes:</strong> {client.notes}
              </div>
            )}
          </div>
        </div>

        {/* Property Map Editor */}
        <PropertyMapEditor
          coordinates={client.propertyCoordinates || []}
          address={client.adresse}
          propertyArea={client.propertyArea || 0}
          onSave={async ({ coordinates, area }) => {
            try {
              await api.put(
                `/api/admin/submissions/${clientId}`,
                {
                  propertyCoordinates: coordinates,
                  propertyArea: area
                },
              );
              
              // Update local state
              setClient({
                ...client,
                propertyCoordinates: coordinates,
                propertyArea: area
              });
              
              alert('✅ La carte de la propriété a été mise à jour avec succès!');
            } catch (error) {
              console.error('Erreur lors de la mise à jour:', error);
              alert('❌ Erreur lors de la sauvegarde de la carte.');
            }
          }}
        />

        {/* Quotes Section */}
        <div className="section">
          <div className="section-header">
            <h2>💼 Devis</h2>
            {!showQuoteForm && (
              <button className="btn-add-quote" onClick={handleNewQuote}>
                <Plus size={20} />
                Nouveau devis
              </button>
            )}
          </div>

          {showQuoteForm ? (
            <div className="quote-form-container">
              <div className="quote-form-header">
                <h3>Nouveau Devis - {client.prenom} {client.nom}</h3>
                <button className="btn-close" onClick={() => setShowQuoteForm(false)}>
                  <X size={20} />
                </button>
              </div>

              <div className="quote-form-body">
                {/* Add Service Button */}
                <button 
                  className="btn-add-service"
                  onClick={() => setShowServiceSelector(true)}
                >
                  <Plus size={18} />
                  Ajouter service ou forfait
                </button>

                {/* Quote Lines Table */}
                {quoteLines.length > 0 && (
                  <div className="quote-table-container">
                    <table className="quote-table">
                      <thead>
                        <tr>
                          <th>Service</th>
                          <th>Description</th>
                          <th>Quantité</th>
                          <th>Prix unitaire</th>
                          <th>Total</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {quoteLines.map((line, index) => (
                          <React.Fragment key={index}>
                            <tr>
                              <td>
                                <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                                  <div 
                                    style={{
                                      width: '8px',
                                      height: '8px',
                                      borderRadius: '50%',
                                      background: line.departement_couleur
                                    }}
                                  />
                                  <span>{line.service_nom}</span>
                                </div>
                              </td>
                              <td>
                                <textarea
                                  value={line.description || ''}
                                  onChange={(e) => handleDescriptionChange(index, e.target.value)}
                                  className="table-textarea"
                                  placeholder="Description pour le contrat..."
                                  rows="2"
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={line.quantite}
                                  onChange={(e) => handleQuantityChange(index, e.target.value)}
                                  className="table-input"
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={line.prix_unitaire}
                                  onChange={(e) => handlePriceChange(index, e.target.value)}
                                  className="table-input"
                                />
                              </td>
                              <td className="total-cell">
                                {(line.quantite * line.prix_unitaire).toLocaleString('fr-CA', {
                                  style: 'currency',
                                  currency: 'CAD'
                                })}
                              </td>
                              <td>
                                <button 
                                  className="btn-remove-line"
                                  onClick={() => handleRemoveLine(index)}
                                  title="Retirer"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </td>
                            </tr>
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>

                    {/* Totals Section */}
                    <div className="quote-totals">
                      <div className="totals-row">
                        <span>Sous-total:</span>
                        <span className="amount">
                          {totals.subtotal.toLocaleString('fr-CA', {
                            style: 'currency',
                            currency: 'CAD'
                          })}
                        </span>
                      </div>

                      {premierIntervenant && (
                        <div className="totals-row discount">
                          <span>Rabais premier intervenant (20%):</span>
                          <span className="amount">
                            -{totals.discount.toLocaleString('fr-CA', {
                              style: 'currency',
                              currency: 'CAD'
                            })}
                          </span>
                        </div>
                      )}

                      <div className="totals-row">
                        <span>TPS (5%):</span>
                        <span className="amount">
                          {totals.tps.toLocaleString('fr-CA', {
                            style: 'currency',
                            currency: 'CAD'
                          })}
                        </span>
                      </div>

                      <div className="totals-row">
                        <span>TVQ (9.975%):</span>
                        <span className="amount">
                          {totals.tvq.toLocaleString('fr-CA', {
                            style: 'currency',
                            currency: 'CAD'
                          })}
                        </span>
                      </div>

                      <div className="totals-row total">
                        <span>TOTAL FINAL:</span>
                        <span className="amount">
                          {totals.total.toLocaleString('fr-CA', {
                            style: 'currency',
                            currency: 'CAD'
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Options */}
                <div className="quote-options">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={premierIntervenant}
                      onChange={(e) => setPremierIntervenant(e.target.checked)}
                    />
                    Premier intervenant (20% de rabais)
                  </label>

                  <div className="form-group">
                    <label>Modalité de paiement:</label>
                    <select 
                      value={modalitePaiement}
                      onChange={(e) => setModalitePaiement(e.target.value)}
                    >
                      <option value="100_avant">100% avant les travaux</option>
                      <option value="60_40">60% avant, 40% après</option>
                    </select>
                  </div>
                </div>

                {/* Action Buttons */}
                {quoteLines.length > 0 && (
                  <div className="quote-actions">
                    <button className="btn-secondary" onClick={() => setShowQuoteForm(false)}>
                      Annuler
                    </button>
                    <button className="btn-save-quote" onClick={handleSaveQuote}>
                      💾 Enregistrer le devis
                    </button>
                    <button className="btn-download-pdf" onClick={handleDownloadNewQuotePDF}>
                      📄 Télécharger PDF
                    </button>
                    <button className="btn-primary" onClick={handleSendNewQuoteEmail}>
                      <Send size={18} />
                      Envoyer par email
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="quotes-list">
              {quotes.length === 0 ? (
                <p>Aucun devis pour ce client</p>
              ) : (
                quotes.map(quote => (
                  <div key={quote.id} className="quote-card">
                    <div className="quote-card-header">
                      <h4>{quote.numero_devis}</h4>
                      <span className={`status-badge ${quote.statut}`}>{quote.statut}</span>
                    </div>
                    <div className="quote-card-body">
                      <p>Date: {new Date(quote.date_creation).toLocaleDateString('fr-CA')}</p>
                      
                      {/* Aperçu des services */}
                      {quote.items && quote.items.length > 0 && (
                        <div className="quote-services-preview">
                          <strong>Services inclus:</strong>
                          <ul className="services-list">
                            {quote.items.slice(0, 3).map((item, idx) => (
                              <li key={idx}>
                                • {item.service_nom || item.nom || 'Service'} - {(item.sous_total || item.prix_total || 0).toLocaleString('fr-CA', {style: 'currency', currency: 'CAD'})}
                              </li>
                            ))}
                            {quote.items.length > 3 && (
                              <li className="more-services">+ {quote.items.length - 3} autre(s) service(s)</li>
                            )}
                          </ul>
                        </div>
                      )}
                      
                      <p className="quote-total">Total: {quote.total_final?.toLocaleString('fr-CA', {style: 'currency', currency: 'CAD'})}</p>
                    </div>
                    <div className="quote-card-actions">
                      <button 
                        className="btn-quote-action edit"
                        onClick={() => {
                          // Charger le devis pour modification
                          const loadedItems = (quote.items || []).map(item => ({
                            ...item,
                            service_id: item.service_id,
                            nom: item.service_nom || item.nom,
                            quantite: item.quantite || 1,
                            prix_unitaire: item.sous_total / (item.quantite || 1) || 0, // Calculer prix unitaire
                            prix_total: item.sous_total || 0
                          }));
                          setQuoteLines(loadedItems);
                          setPremierIntervenant(quote.premier_intervenant || false);
                          setModalitePaiement(quote.modalite_paiement || '100_avant');
                          setShowQuoteForm(true);
                        }}
                        title="Voir / Modifier le devis"
                      >
                        <Edit2 size={18} />
                        Modifier
                      </button>
                      <button 
                        className="btn-quote-action download"
                        onClick={() => handleDownloadPDF(quote.id, quote.numero_devis)}
                        title="Télécharger le PDF"
                      >
                        <Download size={18} />
                        Télécharger PDF
                      </button>
                      <button 
                        className="btn-quote-action email"
                        onClick={() => handleSendEmail(quote.id)}
                        title="Envoyer par email"
                      >
                        <Mail size={18} />
                        Envoyer par email
                      </button>
                      <button 
                        className="btn-quote-action link"
                        onClick={() => handleGeneratePublicLink(quote.id)}
                        title="Générer lien d'acceptation"
                      >
                        <LinkIcon size={18} />
                        {quote.public_token ? 'Copier le lien' : 'Générer lien'}
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Rentabilité Client */}
        <div className="client-profitability-section" style={{ marginTop: '2rem' }}>
          <ClientProfitabilityPanel clientId={clientId} />
        </div>
      </div>

      {/* Service Selector Modal */}
      {showServiceSelector && (
        <div className="modal-overlay" onClick={() => setShowServiceSelector(false)}>
          <div className="service-selector-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Ajouter un service ou forfait</h3>
              <button className="btn-close" onClick={() => setShowServiceSelector(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              {/* Services par département */}
              {Object.entries(groupServicesByDepartment()).map(([deptName, deptData]) => (
                <div key={deptName} className="department-section">
                  <h4 style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                    <div style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      background: deptData.couleur
                    }} />
                    {deptName}
                  </h4>
                  <div className="services-grid">
                    {deptData.services.map(service => (
                      <div 
                        key={service.id}
                        className="service-selector-item"
                        onClick={() => handleAddServiceToQuote(service)}
                      >
                        <div className="service-name">{service.nom}</div>
                        {service.description && (
                          <div className="service-description">{service.description}</div>
                        )}
                        <div className="service-price">
                          {service.prix_vente_par_1000_pi2 > 0 
                            ? `${service.prix_vente_par_1000_pi2.toFixed(2)} $ / 1000 pi²` 
                            : service.prix_base > 0 
                            ? `${service.prix_base.toFixed(2)} $` 
                            : 'Prix sur mesure'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* Section Forfaits */}
              {packages.length > 0 && (
                <div className="department-section forfaits-section">
                  <h4 style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                    <div style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      background: '#F57C00'
                    }} />
                    📦 FORFAITS
                  </h4>
                  <div className="services-grid">
                    {packages.map(forfait => {
                      const dept = departments.find(d => d.id === forfait.department_id);
                      return (
                        <div 
                          key={forfait.id}
                          className="service-selector-item package-item"
                          onClick={() => handleAddPackageToQuote(forfait)}
                        >
                          <div className="service-name">📦 {forfait.nom}</div>
                          {forfait.description && (
                            <div className="service-description">{forfait.description}</div>
                          )}
                          <div className="package-info-badge">
                            {(forfait.services_in_package || []).length} services inclus
                          </div>
                          <div className="service-price">
                            {forfait.prix_vente_par_1000_pi2 > 0 
                              ? `${forfait.prix_vente_par_1000_pi2.toFixed(2)} $ / 1000 pi²` 
                              : 'Prix sur mesure'}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientDetail;
